// 🎫 Coupon Service API
/**
 * 쿠폰 시스템 API 서비스
 * - 사용자 쿠폰 관리
 * - 쿠폰 등록 및 사용
 * - 쿠폰 통계 조회
 * 
 * Author: feature/yeongu/coupon-system
 * Created: 2025-01-27
 * Frontend Version: v1.3.0
 */

import { auth } from '../firebase/config';
import { getApiBaseUrl } from '../utils/apiConfig';
import { getMetaMatchParams } from '../utils/analytics';
import {
  AI30_TRIAL_COUPON_CODES,
  buildAi30TrialAppliedEventId,
  trackAi30TrialAppliedPixel,
} from '../utils/eventTracker';

const REQUEST_TIMEOUT_MS = 30000;
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1500;

const isNetworkError = (error) => {
  const msg = (error.message || '').toLowerCase();
  return msg.includes('failed to fetch') ||
    msg.includes('networkerror') ||
    msg.includes('network request failed') ||
    msg.includes('timeout') ||
    msg.includes('aborted') ||
    msg.includes('load failed') ||
    error.name === 'AbortError';
};

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const fetchWithTimeout = (url, options, timeoutMs = REQUEST_TIMEOUT_MS) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  return fetch(url, { ...options, signal: controller.signal })
    .finally(() => clearTimeout(timer));
};

/**
 * 인증 헤더 생성
 */
const getAuthHeaders = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('사용자 인증이 필요합니다');
  }
  
  const token = await user.getIdToken(true);
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

/**
 * API 요청 래퍼 (타임아웃 + 네트워크 에러 시 자동 재시도)
 */
const apiRequest = async (url, options = {}) => {
  const headers = await getAuthHeaders();
  const fullUrl = `${getApiBaseUrl()}${url}`;
  const reqOptions = {
    ...options,
    headers: { ...headers, ...options.headers }
  };

  let lastError;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const response = await fetchWithTimeout(fullUrl, reqOptions);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      lastError = error;

      const canRetry = attempt < MAX_RETRIES && isNetworkError(error);
      if (!canRetry) break;

      console.warn(`[couponService] 네트워크 오류, ${attempt + 1}/${MAX_RETRIES} 재시도 (${url})`);
      await wait(RETRY_DELAY_MS * (attempt + 1));
    }
  }

  if (isNetworkError(lastError)) {
    throw new Error('네트워크 연결이 불안정합니다. 잠시 후 다시 시도해주세요.');
  }
  throw lastError;
};

// === 사용자 쿠폰 API ===

/**
 * 내 쿠폰 목록 조회
 */
export const getUserCoupons = async (status = null, limit = 50) => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (limit) params.append('limit', limit.toString());
  
  const queryString = params.toString();
  const url = `/api/user-coupons/${queryString ? `?${queryString}` : ''}`;
  
  return await apiRequest(url);
};

/**
 * AI30 캠페인 쿠폰(AI30/TEACHER30) 클레임에만 Meta 매칭 정보를 얹는다.
 * 서버가 같은 event_id(`ai30trial-{uid}`)로 CAPI를 쏘므로 EMQ가 올라간다.
 * 일반 쿠폰은 metadata를 건드리지 않는다.
 */
const enrichAi30ClaimMetadata = (couponCode, currentUser, metadata) => {
  const normalizedCode = String(couponCode || '').trim().toUpperCase();
  if (!AI30_TRIAL_COUPON_CODES.includes(normalizedCode)) {
    return { metadata, ai30EventId: null, normalizedCode };
  }
  const uid = currentUser?.uid || auth.currentUser?.uid || null;
  const ai30EventId = buildAi30TrialAppliedEventId(uid);
  if (!ai30EventId) {
    return { metadata, ai30EventId: null, normalizedCode };
  }
  let matchParams = {};
  try {
    matchParams = getMetaMatchParams();
  } catch { /* 쿠키 접근 실패해도 클레임은 진행 */ }
  return {
    normalizedCode,
    ai30EventId,
    metadata: {
      ...metadata,
      ...matchParams,
      ai30_event_id: ai30EventId,
      event_source_url: `${window.location.origin}${window.location.pathname}`,
    },
  };
};

/**
 * AI30 캠페인 쿠폰이 "신규 적용"됐을 때만 브라우저 픽셀 발화.
 * already_claimed=true(멱등/복구 재호출)면 발화하지 않는다 — 서버 CAPI가
 * 같은 event_id로 커버하고, Meta가 event_id 기준으로 중복 제거한다.
 */
const maybeTrackAi30TrialApplied = (result, normalizedCode, ai30EventId) => {
  try {
    if (ai30EventId && result && result.already_claimed !== true) {
      trackAi30TrialAppliedPixel({ couponCode: normalizedCode, eventId: ai30EventId });
    }
  } catch { /* 트래킹 실패는 쿠폰 흐름을 막지 않는다 */ }
  return result;
};

/**
 * 쿠폰 등록/클레임
 * @param {string} couponCode - 쿠폰 코드
 * @param {Object} currentUser - 현재 로그인된 사용자 (AuthContext에서 전달)
 * @param {Object} metadata - 추가 메타데이터
 */
export const claimCoupon = async (couponCode, currentUser = null, metadata = {}) => {
  const url = '/api/user-coupons/claim';
  const {
    metadata: claimMetadata,
    ai30EventId,
    normalizedCode,
  } = enrichAi30ClaimMetadata(couponCode, currentUser, metadata);
  const body = JSON.stringify({
    coupon_code: couponCode,
    source: 'manual',
    metadata: claimMetadata
  });

  if (currentUser) {
    const token = await currentUser.getIdToken(true);
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };

    let lastError;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const response = await fetchWithTimeout(
          `${getApiBaseUrl()}${url}`,
          { method: 'POST', headers, body }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const httpError = new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
          httpError.status = response.status;
          throw httpError;
        }

        return maybeTrackAi30TrialApplied(await response.json(), normalizedCode, ai30EventId);
      } catch (error) {
        lastError = error;
        const canRetry = attempt < MAX_RETRIES && isNetworkError(error);
        if (!canRetry) break;

        console.warn(`[claimCoupon] 네트워크 오류, ${attempt + 1}/${MAX_RETRIES} 재시도`);
        await wait(RETRY_DELAY_MS * (attempt + 1));
      }
    }

    if (isNetworkError(lastError)) {
      const networkError = new Error('네트워크 연결이 불안정합니다. 잠시 후 다시 시도해주세요.');
      networkError.retryable = true;
      throw networkError;
    }
    throw lastError;
  }

  return maybeTrackAi30TrialApplied(
    await apiRequest(url, { method: 'POST', body }),
    normalizedCode,
    ai30EventId,
  );
};

// 잠깐의 오류면 저장해 둔 쿠폰을 지우지 않고 다음 로그인·방문 때 다시 등록한다.
export const isRetryableCouponError = (error) => (
  error?.retryable === true || error?.status === 429 || Number(error?.status) >= 500
);

/**
 * 쿠폰 사용
 */
export const applyCoupon = async (userCouponId, metadata = {}) => {
  return await apiRequest('/api/user-coupons/use', {
    method: 'POST',
    body: JSON.stringify({
      user_coupon_id: userCouponId,
      metadata
    })
  });
};

/**
 * 쿠폰 통계 조회
 */
export const getCouponStats = async () => {
  return await apiRequest('/api/user-coupons/stats');
};

/**
 * 특정 쿠폰 상세 조회
 */
export const getUserCoupon = async (userCouponId) => {
  return await apiRequest(`/api/user-coupons/${userCouponId}`);
};

/**
 * 만료된 쿠폰 확인 및 업데이트
 */
export const checkExpiredCoupons = async () => {
  return await apiRequest('/api/user-coupons/expire-check', {
    method: 'POST'
  });
};

// === 쿠폰 상태 및 유틸리티 ===

/**
 * 쿠폰 상태 한글 변환
 */
export const getCouponStatusText = (status) => {
  const statusMap = {
    'available': '사용 가능',
    'used': '사용 완료',
    'expired': '만료됨',
    'pending': '대기중'
  };
  
  return statusMap[status] || status;
};

/**
 * 쿠폰 소스 한글 변환
 */
export const getCouponSourceText = (source) => {
  const sourceMap = {
    'manual': '수동 등록',
    'referral': '추천 보상',
    'event': '이벤트',
    'promotion': '프로모션',
    'admin': '관리자 지급',
    'purchase': '구매 보상'
  };
  
  return sourceMap[source] || source;
};

/**
 * 쿠폰 타입 한글 변환
 */
export const getCouponTypeText = (type) => {
  const typeMap = {
    'subscription': 'PRO 혜택',
    'discount': '할인',
    'free_trial': '무료 체험',
    'bonus': '보너스'
  };
  
  return typeMap[type] || type;
};

/**
 * 남은 일수 계산
 */
export const getDaysRemaining = (expiresAt) => {
  if (!expiresAt) return null;
  
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diffTime = expiry - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays > 0 ? diffDays : 0;
};

/**
 * 쿠폰 만료 여부 확인
 */
export const isCouponExpired = (expiresAt) => {
  if (!expiresAt) return false;
  return new Date(expiresAt) <= new Date();
};

/**
 * 쿠폰 사용 가능 여부 확인
 */
export const isCouponUsable = (coupon) => {
  return coupon.status === 'available' && !isCouponExpired(coupon.expires_at);
};

/**
 * 날짜 포맷팅
 */
export const formatDate = (dateString) => {
  if (!dateString) return '-';
  
  const date = new Date(dateString);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * 쿠폰 혜택 기간 포맷팅
 */
export const formatBenefitPeriod = (benefitStartsAt, benefitEndsAt) => {
  if (!benefitStartsAt || !benefitEndsAt) return '-';
  
  const start = new Date(benefitStartsAt);
  const end = new Date(benefitEndsAt);
  
  return `${start.toLocaleDateString('ko-KR')} ~ ${end.toLocaleDateString('ko-KR')}`;
};

// === 에러 처리 ===

/**
 * API 에러 메시지 파싱
 */
export const parseErrorMessage = (error) => {
  if (typeof error === 'string') return error;
  if (error.message) return error.message;
  if (error.detail) return error.detail;
  return '알 수 없는 오류가 발생했습니다';
};

/**
 * 쿠폰 관련 에러 메시지 개선
 *
 * 백엔드 detail 패턴과 매칭되는 사용자 친화 문구를 반환한다.
 * 매칭 우선순위는 위에서 아래로(가장 구체적인 케이스가 먼저).
 *
 * 백엔드 거부 detail 형태 (참고):
 *  - "쿠폰 'XXX'를 찾을 수 없습니다"
 *  - "사용할 수 없는 쿠폰입니다: 만료된 쿠폰입니다"
 *  - "사용할 수 없는 쿠폰입니다: 사용량이 소진된 쿠폰입니다"
 *  - "사용할 수 없는 쿠폰입니다: 비활성화된 쿠폰입니다"
 *  - "사용할 수 없는 쿠폰입니다: 쿠폰 사용 가능일: 2026-12-25"
 *  - "쿠폰 획득 검증 실패: 사용량 한도에 도달한 쿠폰입니다"
 *  - "쿠폰 획득 검증 실패: 아직 사용할 수 없는 쿠폰입니다"
 *  - "이미 동일한 유형의 쿠폰을 사용하셨습니다..."  (FREE 사용자 그룹 중복)
 *  - "같은 유형의 쿠폰을 이미 사용하셨어요. 현재 PRO가 이미 적용되어..."  (PRO 사용자 그룹 중복)
 */
export const getCouponErrorMessage = (error) => {
  const message = parseErrorMessage(error);
  const lower = message.toLowerCase();

  if (lower.includes('already claimed')) {
    return '이미 등록된 쿠폰입니다.';
  }
  if (lower.includes('not found') || message.includes('찾을 수 없습니다')) {
    return '존재하지 않는 쿠폰 코드입니다. 코드를 다시 확인해주세요.';
  }

  // PRO 사용자가 같은 그룹의 다른 쿠폰을 등록 시도하는 경우.
  // 백엔드는 한국어로 "같은 유형의 쿠폰을 이미 사용하셨어요. 현재 PRO가 이미 적용되어..."
  // 라는 문장을 보내므로, "유형" + "PRO" 조합으로 식별 (다른 매핑보다 먼저 평가).
  if (message.includes('유형') && message.includes('PRO')) {
    return '같은 유형의 쿠폰을 이미 사용하셨어요. 현재 PRO가 적용 중이니 그대로 이용해주세요.';
  }
  if (message.includes('동일한 유형') || message.includes('같은 그룹') || message.includes('같은 유형')) {
    return '같은 유형의 쿠폰을 이미 사용하셨어요. 같은 그룹의 쿠폰은 1인 1회만 사용할 수 있습니다.';
  }

  if (lower.includes('expired') || message.includes('만료')) {
    return '만료된 쿠폰입니다.';
  }
  if (lower.includes('already used') || message.includes('이미 사용된')) {
    return '이미 사용된 쿠폰입니다.';
  }

  // 수량 소진: 백엔드의 두 코드 경로(can_be_used / validate_coupon_claim)가 표현이 다르므로
  // "사용량" 키워드로 묶어 매핑한다.
  if (message.includes('사용량')) {
    return '쿠폰 사용량이 모두 소진되었어요. 다른 쿠폰을 사용하시거나 고객센터로 문의해주세요.';
  }

  // 시작일 미도래: 백엔드는 "쿠폰 사용 가능일: YYYY-MM-DD" 또는 "아직 사용할 수 없는 쿠폰입니다" 둘 다 보낼 수 있다.
  if (message.includes('아직 사용할 수 없') || message.includes('사용 가능일')) {
    const dateMatch = message.match(/\d{4}-\d{2}-\d{2}/);
    if (dateMatch) {
      return `아직 사용할 수 없는 쿠폰입니다. ${dateMatch[0]}부터 사용 가능합니다.`;
    }
    return '아직 사용할 수 없는 쿠폰입니다. 사용 가능 시점을 다시 확인해주세요.';
  }

  // 비활성 쿠폰: "비활성화된 쿠폰입니다" / "비활성 상태의 쿠폰입니다"
  if (message.includes('비활성')) {
    return '현재 사용할 수 없는 쿠폰이에요. 고객센터로 문의해주세요.';
  }

  if (lower.includes('unauthorized') || message.includes('인증')) {
    return '로그인이 필요합니다. 페이지를 새로고침 후 다시 시도해주세요.';
  }
  if (lower.includes('네트워크') || lower.includes('failed to fetch') || lower.includes('network') || lower.includes('timeout') || lower.includes('aborted')) {
    return '네트워크 연결이 불안정합니다. 인터넷 연결을 확인하고 다시 시도해주세요.';
  }

  return message;
};